import { useCallback, useEffect, useState } from "react";
import { Activity, RefreshCw, Database, TrendingUp, Image as ImageIcon, ShieldCheck, ShieldAlert, Server } from "lucide-react";
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Empty } from "@/components/ui/empty";
import { api, type FullStats } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/cn";

const REFRESH_MS = 30_000;

/**
 * Tableau de bord couverture + santé (consomme /api/stats/full).
 * Page technique → libellés en anglais en dur (dates localisées via fmtDate).
 */
export function Stats() {
  const { fmtDate } = useI18n();
  const [data, setData] = useState<FullStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api<FullStats>("/api/stats/full"));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  if (loading && !data) {
    return <div className="grid place-items-center py-20 text-sm text-muted-fg">Loading…</div>;
  }
  if (error && !data) {
    return (
      <Card>
        <CardBody><Empty title="Failed to load stats" description={error} /></CardBody>
      </Card>
    );
  }
  if (!data) return null;

  const pct = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0);
  const dbUp = data.health.db === "up";
  const dq = data.data_quality;
  const dqClean = (dq.stat_rows || 0) === 0 && (dq.total_rows || 0) === 0;
  const job = data.job;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wide text-muted-fg">
            <Activity className="h-3.5 w-3.5" />
            Stats &amp; health
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Coverage &amp; application health</h1>
          <p className="mt-1 text-xs text-muted-fg">
            Updated {fmtDate(data.generated_at)} · auto-refresh 30s
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={dbUp ? "success" : "danger"} variant="soft">
            <Server className="h-3 w-3" /> DB {dbUp ? "up" : "down"}
          </Badge>
          {job?.running ? (
            <Badge tone="info" variant="soft">
              <RefreshCw className="h-3 w-3 animate-spin" /> batch: {job.currentScope}{job.currentFilter ? ` · ${job.currentFilter}` : ""}
            </Badge>
          ) : null}
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        </div>
      </header>

      {/* KPI row */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={<Database className="h-4 w-4" />} label="Cards indexed" value={data.cards.total.toLocaleString()} />
        <Kpi
          icon={<TrendingUp className="h-4 w-4" />}
          label="Cards priced"
          value={data.coverage.priced_cards.toLocaleString()}
          hint={`${pct(data.coverage.priced_cards, data.coverage.total_cards)}% of catalog`}
        />
        <Kpi
          icon={<RefreshCw className="h-4 w-4" />}
          label="Fresh (< 2d)"
          value={data.freshness.fresh_2d.toLocaleString()}
          hint={`${pct(data.freshness.fresh_2d, data.freshness.total)}% of priced`}
          tone={data.freshness.fresh_2d > 0 ? "success" : "warn"}
        />
        <Kpi
          icon={<ImageIcon className="h-4 w-4" />}
          label="Priced with image"
          value={data.metadata.priced_with_image.toLocaleString()}
          hint={`${pct(data.metadata.priced_with_image, data.metadata.priced)}% of priced`}
          tone="warn"
        />
      </div>

      {/* Freshness + data quality */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <div>
              <CardTitle>Price freshness</CardTitle>
              <CardDescription>
                {data.freshness.total.toLocaleString()} priced cards · oldest {fmtDate(data.freshness.oldest)}
              </CardDescription>
            </div>
          </CardHeader>
          <CardBody className="space-y-3">
            <FreshnessBar
              total={data.freshness.total}
              fresh2d={data.freshness.fresh_2d}
              fresh7d={data.freshness.fresh_7d}
            />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Mini label="< 2 days" value={data.freshness.fresh_2d} tone="success" />
              <Mini label="< 7 days" value={data.freshness.fresh_7d} tone="info" />
              <Mini label="> 7 days" value={data.freshness.stale_7d} tone="warn" />
              <Mini label="> 30 days" value={data.freshness.stale_30d} tone="danger" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <div>
              <CardTitle>Data quality</CardTitle>
              <CardDescription>Aggregate pseudo-prices leaking as observations</CardDescription>
            </div>
          </CardHeader>
          <CardBody>
            <div className={cn(
              "flex items-center gap-3 rounded-md border p-4",
              dqClean ? "border-success/40 bg-success/5" : "border-danger/40 bg-danger/5",
            )}>
              {dqClean ? <ShieldCheck className="h-6 w-6 text-success" /> : <ShieldAlert className="h-6 w-6 text-danger" />}
              <div>
                <div className="text-sm font-semibold">{dqClean ? "Clean" : "Leaks detected"}</div>
                <div className="text-xs text-muted-fg">
                  STAT_*: <span className="font-mono">{dq.stat_rows}</span> · total: <span className="font-mono">{dq.total_rows}</span>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Coverage by language */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Coverage by language</CardTitle>
            <CardDescription>Cards, priced cards, and 7-day freshness per language</CardDescription>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <Table>
            <THead>
              <TR>
                <TH>Language</TH>
                <TH className="text-right">Cards</TH>
                <TH className="text-right">Priced</TH>
                <TH className="text-right">Coverage</TH>
                <TH className="text-right">Fresh &lt; 7d</TH>
              </TR>
            </THead>
            <TBody>
              {data.by_language.map((l) => (
                <TR key={l.language}>
                  <TD><Badge tone="neutral">{l.language}</Badge></TD>
                  <TD className="text-right font-mono">{l.cards.toLocaleString()}</TD>
                  <TD className="text-right font-mono">{l.priced.toLocaleString()}</TD>
                  <TD className="text-right font-mono text-muted-fg">{pct(l.priced, l.cards)}%</TD>
                  <TD className="text-right font-mono">
                    <span className={l.fresh_7d > 0 ? "text-success" : "text-muted-fg"}>{l.fresh_7d.toLocaleString()}</span>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </CardBody>
      </Card>

      {/* Coverage by source */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Sources (current snapshot)</CardTitle>
            <CardDescription>Observations, distinct cards, and freshness per source</CardDescription>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <Table>
            <THead>
              <TR>
                <TH>Source</TH>
                <TH className="text-right">Observations</TH>
                <TH className="text-right">Cards</TH>
                <TH className="text-right">Fresh &lt; 2d</TH>
                <TH className="text-right">Stale &gt; 7d</TH>
                <TH className="text-right">Last seen</TH>
              </TR>
            </THead>
            <TBody>
              {data.by_source.map((s) => (
                <TR key={s.source}>
                  <TD className="font-medium">{s.source}</TD>
                  <TD className="text-right font-mono">{s.observations.toLocaleString()}</TD>
                  <TD className="text-right font-mono">{s.cards.toLocaleString()}</TD>
                  <TD className="text-right font-mono text-success">{s.fresh_2d.toLocaleString()}</TD>
                  <TD className="text-right font-mono text-warn">{s.stale_7d.toLocaleString()}</TD>
                  <TD className="text-right text-xs text-muted-fg">{fmtDate(s.last_seen)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </CardBody>
      </Card>

      {/* Last runs per source */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Last run per source</CardTitle>
            <CardDescription>Most recent batch run status for each registered source</CardDescription>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {data.last_runs.length === 0 ? (
            <div className="p-6"><Empty title="No runs recorded" /></div>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Source</TH>
                  <TH>Status</TH>
                  <TH className="text-right">Upserts</TH>
                  <TH className="text-right">Duration</TH>
                  <TH className="text-right">Started</TH>
                </TR>
              </THead>
              <TBody>
                {data.last_runs.map((r) => (
                  <TR key={r.source_key}>
                    <TD className="font-mono text-xs">{r.source_key}</TD>
                    <TD>
                      <Badge tone={r.success ? "success" : "danger"} variant="soft">
                        {r.success ? "ok" : `exit ${r.exit_code ?? "?"}`}
                      </Badge>
                    </TD>
                    <TD className="text-right font-mono">{r.value_upserts ?? "—"}</TD>
                    <TD className="text-right font-mono text-muted-fg">{r.duration_s != null ? `${r.duration_s}s` : "—"}</TD>
                    <TD className="text-right text-xs text-muted-fg">{fmtDate(r.started_at)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Kpi({
  icon,
  label,
  value,
  hint,
  tone = "primary",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  tone?: "primary" | "success" | "warn";
}) {
  const toneCls =
    tone === "success" ? "text-success" : tone === "warn" ? "text-warn" : "text-primary";
  return (
    <div className="rounded-lg border border-border bg-surface p-4 shadow-soft">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wide text-muted-fg">{label}</div>
        <span className={cn("grid h-7 w-7 place-items-center rounded-md bg-surface-2", toneCls)}>{icon}</span>
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums">{value}</div>
      {hint ? <div className="mt-0.5 text-[11px] text-muted-fg">{hint}</div> : null}
    </div>
  );
}

function Mini({ label, value, tone }: { label: string; value: number; tone: "success" | "info" | "warn" | "danger" }) {
  const toneCls = {
    success: "text-success",
    info: "text-info",
    warn: "text-warn",
    danger: "text-danger",
  }[tone];
  return (
    <div className="rounded-md border border-border bg-surface p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-fg">{label}</div>
      <div className={cn("mt-1 text-lg font-semibold tabular-nums", toneCls)}>{value.toLocaleString()}</div>
    </div>
  );
}

function FreshnessBar({ total, fresh2d, fresh7d }: { total: number; fresh2d: number; fresh7d: number }) {
  if (total <= 0) return null;
  const p2 = (fresh2d / total) * 100;
  const p7 = Math.max(0, ((fresh7d - fresh2d) / total) * 100);
  const pStale = Math.max(0, 100 - p2 - p7);
  return (
    <div className="flex h-3 w-full overflow-hidden rounded-full bg-surface-2" title={`${fresh2d} < 2d · ${fresh7d} < 7d · ${total} total`}>
      <div className="bg-success" style={{ width: `${p2}%` }} />
      <div className="bg-info" style={{ width: `${p7}%` }} />
      <div className="bg-warn/40" style={{ width: `${pStale}%` }} />
    </div>
  );
}
